import { AUJS_FORMS } from '@app/forms/shared/aujs-forms';
import { AuthorizationService } from '@app/security/shared/authorization.service';
import { Form } from '@app/forms/form.model';
import { GuidService } from '@app/shared/guid.service';
import { IMenu, MenuConstructorParams } from '@app/menu/shared/menu.interface';
import { Injectable } from '@angular/core';
import { LegacyService } from '@app/legacy/legacy.service';
import { Menu } from '@app/menu/shared/menu.model';

@Injectable({
  providedIn: 'root'
})
export class MenuService {
  constructor(
    private authorizationService: AuthorizationService,
    private legacyService: LegacyService,
    private guidService: GuidService
  ) {}

  private getFormMenuObject(formEnumId: string): IMenu {
    const form = AUJS_FORMS[formEnumId];
    const menuParams: MenuConstructorParams = {};
    if (form) {
      let alwaysShow: boolean = form.alwaysShow;
      if (this.legacyService.hideLegacyForms && form.legacy) {
        alwaysShow = false;
      }

      menuParams.name = form.name;
      menuParams.type = 'form';
      menuParams.enabled = form.enabled;
      menuParams.formEnumId = form.enumId;
      menuParams.formId = form.id;
      menuParams.alwaysShow = alwaysShow;
    } else {
      menuParams.name = formEnumId;
      menuParams.type = 'form';
      menuParams.enabled = false;
      menuParams.formEnumId = formEnumId;
    }

    return new Menu(menuParams);
  }

  public getMenuItems(): Array<IMenu> {
    return this.getFullMenu().filter((item: IMenu) => this.itemShouldBeShown(item));
  }

  private getSectionObject(name: string, children: Array<IMenu>, icon: string): IMenu {
    const menuParams: MenuConstructorParams = {};

    menuParams.name = name;
    menuParams.type = 'section';
    menuParams.enabled = true;
    menuParams.formEnumId = null;
    menuParams.children = children;
    menuParams.icon = icon;
    menuParams.uuid = this.guidService.newGuid();

    return new Menu(menuParams);
  }

  private itemShouldBeShown(item: IMenu): boolean {
    switch (item?.type) {
      case 'form':
        if (this.legacyService.hideLegacyForms) {
          const forms: Array<Form> = Object.values(AUJS_FORMS);
          const form: Form = forms.find((formItem: Form) => formItem.enumId === item.formEnumId);

          if (form && form.legacy) {
            return false;
          }
        }

        return (
          this.authorizationService.getFormPermission(item.formId).userCanView() || item.alwaysShow
        );
      case 'section':
        item.children = item.children.filter((children: IMenu) => this.itemShouldBeShown(children));
        return item.children.length > 0;
    }

    return false;
  }

  private getFullMenu(): Array<IMenu> {
    return [
      this.getSalesMenu(),
      this.getTicketProceduresMenu(),
      this.getOperationsMenu(),
      this.getInventoryMenu(),
      this.getCreditMenu(),
      this.getFormMenuObject('FormNgReports'),
      this.getFormMenuObject('FormReports'),
      this.getFormMenuObject('FormAbout')
    ];
  }

  private getSalesMenu(): IMenu {
    if (this.legacyService.hideLegacyForms) {
      return this.getSectionObject(
        'Sales',
        [
          this.getSectionObject(
            'Audit Orders',
            [
              this.getFormMenuObject('FormNgAuditOrderConcrete'),
              this.getFormMenuObject('FormNgAuditOrderAggregate'),
              this.getFormMenuObject('FormNgAuditOrderBlock')
            ],
            undefined
          ),
          this.getFormMenuObject('FormNgQuoteMaintenance'),
          this.getFormMenuObject('FormNgJobMaintenance'),
          this.getSectionObject(
            'Product Maintenance',
            [
              this.getFormMenuObject('FormNgConcreteProductMaintenance'),
              this.getFormMenuObject('FormNgAggregateProductMaintenance'),
              this.getFormMenuObject('FormNgBlockProductMaintenance'),
              this.getFormMenuObject('FormNgOtherProductMaintenance'),
              this.getFormMenuObject('FormNgRawMaterialProductMaintenance')
            ],
            undefined
          )
        ],
        'fa-usd'
      );
    } else {
      return this.getSectionObject(
        'Sales',
        [
          this.getSectionObject(
            'AUJS 2',
            [
              this.getSectionObject(
                'Audit Orders',
                [
                  this.getFormMenuObject('FormNgAuditOrderConcrete'),
                  this.getFormMenuObject('FormNgAuditOrderAggregate'),
                  this.getFormMenuObject('FormNgAuditOrderBlock')
                ],
                undefined
              ),
              this.getFormMenuObject('FormNgQuoteMaintenance'),
              this.getFormMenuObject('FormNgJobMaintenance'),
              this.getSectionObject(
                'Product Maintenance',
                [
                  this.getFormMenuObject('FormNgConcreteProductMaintenance'),
                  this.getFormMenuObject('FormNgAggregateProductMaintenance'),
                  this.getFormMenuObject('FormNgBlockProductMaintenance'),
                  this.getFormMenuObject('FormNgOtherProductMaintenance'),
                  this.getFormMenuObject('FormNgRawMaterialProductMaintenance')
                ],
                undefined
              )
            ],
            undefined
          ),

          this.getSectionObject(
            'Classic',
            [
              this.getFormMenuObject('FormProduct'),
              this.getFormMenuObject('FormBid'),
              this.getFormMenuObject('FormQuote'),
              this.getFormMenuObject('FormJob'),
              this.getSectionObject(
                'Classic Audit Orders',
                [
                  this.getFormMenuObject('FormAuditOrderConcrete'),
                  this.getFormMenuObject('FormAuditOrderAggregate'),
                  this.getFormMenuObject('FormAuditOrderBlock')
                ],
                undefined
              ),
              this.getFormMenuObject('FormDirections')
            ],
            undefined
          )
        ],
        'fa-usd'
      );
    }
  }

  private getTicketProceduresMenu(): IMenu {
    if (this.legacyService.hideLegacyForms) {
      return this.getSectionObject(
        'Ticket Proc.',
        [
          this.getFormMenuObject('FormNgEditTickets'),
          this.getFormMenuObject('FormNgBatchEditTicket'),
          this.getFormMenuObject('FormNgInvoiceFlag'),
          this.getFormMenuObject('FormNgInvoiceInquiry'),
          this.getFormMenuObject('FormNgTicketInquiry')
        ],
        'fa-columns'
      );
    } else {
      return this.getSectionObject(
        'Ticket Proc.',
        [
          this.getSectionObject(
            'AUJS 2',
            [
              this.getFormMenuObject('FormNgEditTickets'),
              this.getFormMenuObject('FormNgBatchEditTicket'),
              this.getFormMenuObject('FormNgInvoiceFlag'),
              this.getFormMenuObject('FormNgInvoiceInquiry'),
              this.getFormMenuObject('FormNgTicketInquiry')
            ],
            undefined
          ),

          this.getSectionObject(
            'Classic',
            [
              this.getFormMenuObject('FormTicketsReceive'),
              this.getFormMenuObject('FormTicketEdit'),
              this.getFormMenuObject('FormTicketEditBatch'),
              this.getFormMenuObject('FormTicketsExport'),
              this.getFormMenuObject('FormInvoiceFlag'),
              this.getFormMenuObject('FormTicketInquiry'),
              this.getFormMenuObject('FormInvoiceInquiry')
            ],
            undefined
          )
        ],
        'fa-columns'
      );
    }
  }

  private getOperationsMenu(): IMenu {
    if (this.legacyService.hideLegacyForms) {
      return this.getSectionObject(
        'Operations',
        [
          this.getSectionObject(
            'Concrete',
            [this.getFormMenuObject('FormNgOrderEntryConcrete')],
            undefined
          ),
          this.getSectionObject(
            'Aggregate',
            [
              this.getFormMenuObject('FormNgOrderEntryAggregate'),
              this.getFormMenuObject('FormNgAggregateTicketing')
            ],
            undefined
          ),
          this.getSectionObject(
            'Block',
            [this.getFormMenuObject('FormNgOrderEntryBlock')],
            undefined
          ),
          this.getFormMenuObject('FormNgLoadSchedulingScreen'),
          this.getFormMenuObject('FormNgTruckMaintenance'),
          this.getFormMenuObject('FormNgBatchLoadScreen'),
          this.getFormMenuObject('FormNgPlantMaintenance'),
          this.getFormMenuObject('FormNgPumperMaintenance'),
          this.getFormMenuObject('FormNgEmployeeMaintenance')
        ],
        'fa-cogs'
      );
    } else {
      return this.getSectionObject(
        'Operations',
        [
          this.getSectionObject(
            'AUJS 2',
            [
              this.getSectionObject(
                'Concrete',
                [this.getFormMenuObject('FormNgOrderEntryConcrete')],
                undefined
              ),
              this.getSectionObject(
                'Aggregate',
                [
                  this.getFormMenuObject('FormNgOrderEntryAggregate'),
                  this.getFormMenuObject('FormNgAggregateTicketing')
                ],
                undefined
              ),
              this.getSectionObject(
                'Block',
                [this.getFormMenuObject('FormNgOrderEntryBlock')],
                undefined
              ),
              this.getFormMenuObject('FormNgLoadSchedulingScreen'),
              this.getFormMenuObject('FormNgTruckMaintenance'),
              this.getFormMenuObject('FormNgBatchLoadScreen'),
              this.getFormMenuObject('FormNgPlantMaintenance'),
              this.getFormMenuObject('FormNgPumperMaintenance'),
              this.getFormMenuObject('FormNgEmployeeMaintenance')
            ],
            undefined
          ),

          this.getSectionObject(
            'Classic',
            [
              this.getSectionObject(
                'Concrete',
                [
                  this.getFormMenuObject('FormOrderEntryConcreteBasic'),
                  this.getFormMenuObject('FormOrderEntryConcrete'),
                  this.getFormMenuObject('FormTicketConcrete'),
                  this.getFormMenuObject('FormCalculatorConcrete'),
                  this.getFormMenuObject('FormMixDesignModification'),
                  this.getFormMenuObject('FormMixDesignChange'),
                  this.getFormMenuObject('FormRawMaterialRanking'),
                  this.getFormMenuObject('FormAdmixDosingByTravelMinutes')
                ],
                undefined
              ),
              this.getSectionObject(
                'Aggregate',
                [
                  this.getFormMenuObject('FormOrderEntryAggregate'),
                  this.getFormMenuObject('FormTicketAggregate'),
                  this.getFormMenuObject('FormTicketAggregateQuick'),
                  this.getFormMenuObject('FormDeliveryCharge'),
                  this.getFormMenuObject('FormMixDesignAggregate'),
                  this.getFormMenuObject('FormTrailer'),
                  this.getFormMenuObject('FormOrderEntryRail'),
                  this.getFormMenuObject('FormScheduleRail'),
                  this.getFormMenuObject('FormCommodityCodeRail')
                ],
                undefined
              ),
              this.getSectionObject(
                'Block',
                [
                  this.getFormMenuObject('FormOrderEntryBlock'),
                  this.getFormMenuObject('FormTicketBlock'),
                  this.getFormMenuObject('FormCalculatorBlock')
                ],
                undefined
              ),
              this.getFormMenuObject('FormLoadSchedulingScreen'),
              this.getFormMenuObject('FormTruckDemand'),
              this.getFormMenuObject('FormTruckDemandManager'),
              this.getFormMenuObject('FormLoadDemand'),
              this.getFormMenuObject('FormTruck'),
              this.getFormMenuObject('FormMapTravelTimes'),
              this.getFormMenuObject('FormTicketEditTimes'),
              this.getFormMenuObject('FormTruckCompany'),
              this.getFormMenuObject('FormBatchLoadScreen'),
              this.getFormMenuObject('FormBatchLoadScreenBlock'),
              this.getFormMenuObject('FormPlant'),
              this.getFormMenuObject('FormPumper'),
              this.getFormMenuObject('FormEmployee'),
              this.getFormMenuObject('FormEmployeeTimes')
            ],
            undefined
          )
        ],
        'fa-cogs'
      );
    }
  }

  private getInventoryMenu(): IMenu {
    return this.getSectionObject(
      'Inventory',
      [
        this.getFormMenuObject('FormVendor'),
        this.getFormMenuObject('FormHauler'),
        this.getFormMenuObject('FormSource'),
        this.getFormMenuObject('FormMaterialReceipt'),
        this.getFormMenuObject('FormMaterialReceiptsReceive'),
        this.getFormMenuObject('FormMaterialInvoice'),
        this.getFormMenuObject('FormInventoryAdjustment'),
        this.getFormMenuObject('FormInventoryPhysicalCount'),
        this.getFormMenuObject('FormInventoryPhysicalCountBatch')
      ],
      'fa-th-list'
    );
  }

  private getCreditMenu(): IMenu {
    if (this.legacyService.hideLegacyForms) {
      return this.getSectionObject(
        'Credit',
        [this.getFormMenuObject('FormNgCustomerMaintenance')],
        'fa-plus-square'
      );
    } else {
      return this.getSectionObject(
        'Credit',
        [
          this.getSectionObject(
            'AUJS 2',
            [this.getFormMenuObject('FormNgCustomerMaintenance')],
            undefined
          ),

          this.getSectionObject(
            'Classic',
            [
              this.getFormMenuObject('FormCustomer'),
              this.getFormMenuObject('FormLabels'),
              this.getFormMenuObject('FormLetterMaintenance'),
              this.getFormMenuObject('FormLetter'),
              this.getFormMenuObject('FormPrelim'),
              this.getFormMenuObject('FormPrelimPrint')
            ],
            undefined
          )
        ],
        'fa-plus-square'
      );
    }
  }
}
